// ExperimentLedger.js — Full events table with party filter and CSV export.
// Columns: date, type, party, CHF, KES, FX rate, description.

import React, { useState, useMemo, useCallback } from 'react';
import { Download, Filter } from 'lucide-react';

const PARTY_OPTIONS = [
  { key: 'all', label: 'All Parties' },
  { key: 'investor', label: 'Investor' },
  { key: 'forwardsflow', label: 'Forwardsflow' },
  { key: 'swift', label: 'SWIFT' },
  { key: 'tax', label: 'Tax' },
  { key: 'cbk', label: 'CBK' },
];

const PARTY_COLORS = {
  investor: 'text-blue-600',
  forwardsflow: 'text-indigo-600',
  swift: 'text-amber-600',
  tax: 'text-red-600',
  cbk: 'text-emerald-600',
};

function fmtAmount(value) {
  if (value == null) return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  const sign = num < 0 ? '-' : '+';
  return `${sign}${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function amountColor(value) {
  if (value == null) return 'text-zinc-400';
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) return 'text-zinc-400';
  return num > 0 ? 'text-emerald-600' : 'text-red-600';
}

function fmtRate(value) {
  if (value == null) return '';
  return parseFloat(value).toFixed(4);
}

function fmtDate(dateStr) {
  if (!dateStr) return '--';
  return dateStr.split('T')[0];
}

function formatEventType(type) {
  if (!type) return '--';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function ExperimentLedger({ events = [], onExport }) {
  const [partyFilter, setPartyFilter] = useState('all');

  const filteredEvents = useMemo(() => {
    if (partyFilter === 'all') return events;
    return events.filter(e => e.party === partyFilter);
  }, [events, partyFilter]);

  const handleExport = useCallback(() => {
    if (onExport) {
      onExport();
      return;
    }

    // Client-side CSV fallback
    const headers = ['Date', 'Type', 'Party', 'CHF', 'KES', 'FX Rate', 'Description'];
    const rows = filteredEvents.map(evt => [
      fmtDate(evt.event_date),
      evt.event_type || '',
      evt.party || '',
      evt.amount_chf != null ? parseFloat(evt.amount_chf).toFixed(2) : '',
      evt.amount_kes != null ? parseFloat(evt.amount_kes).toFixed(2) : '',
      evt.fx_rate != null ? parseFloat(evt.fx_rate).toFixed(4) : '',
      `"${(evt.description || '').replace(/"/g, '""')}"`,
    ].join(','));

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'experiment-ledger.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredEvents, onExport]);

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
          Experiment Ledger
        </span>
        <span className="font-mono text-[10px] text-zinc-400">
          {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between mb-3">
        {/* Party filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-zinc-400" />
          <div className="inline-flex bg-zinc-100 p-0.5">
            {PARTY_OPTIONS.map(opt => (
              <button
                key={opt.key}
                onClick={() => setPartyFilter(opt.key)}
                className={`px-2 py-1 font-mono text-[10px] uppercase tracking-[0.1em] transition-colors ${
                  partyFilter === opt.key
                    ? 'bg-white shadow-sm text-zinc-900'
                    : 'text-zinc-500 hover:text-zinc-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Export */}
        <button
          onClick={handleExport}
          disabled={filteredEvents.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] border border-zinc-200 hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ color: '#4D4D4D' }}
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>

      {/* Events table */}
      {filteredEvents.length === 0 ? (
        <div className="text-center py-8">
          <p className="font-mono text-sm text-zinc-400">
            {events.length === 0 ? 'No events recorded yet.' : 'No events match the selected filter.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b border-zinc-200">
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                  Date
                </th>
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                  Type
                </th>
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                  Party
                </th>
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-right py-2 px-2 font-medium">
                  CHF
                </th>
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-right py-2 px-2 font-medium">
                  KES
                </th>
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-right py-2 px-2 font-medium">
                  FX Rate
                </th>
                <th className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 text-left py-2 px-2 font-medium">
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.map((evt, i) => (
                <tr key={evt.id || i} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                  <td className="py-1.5 px-2 font-mono tabular-nums text-zinc-700 whitespace-nowrap">
                    {fmtDate(evt.event_date)}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-zinc-700">
                    {formatEventType(evt.event_type)}
                  </td>
                  <td className={`py-1.5 px-2 font-mono font-medium ${PARTY_COLORS[evt.party] || 'text-zinc-600'}`}>
                    {evt.party ? evt.party.charAt(0).toUpperCase() + evt.party.slice(1) : '--'}
                  </td>
                  <td className={`py-1.5 px-2 text-right font-mono tabular-nums ${amountColor(evt.amount_chf)}`}>
                    {fmtAmount(evt.amount_chf)}
                  </td>
                  <td className={`py-1.5 px-2 text-right font-mono tabular-nums ${amountColor(evt.amount_kes)}`}>
                    {fmtAmount(evt.amount_kes)}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono tabular-nums text-zinc-500">
                    {fmtRate(evt.fx_rate)}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-zinc-500 text-xs max-w-xs truncate">
                    {evt.description || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
